import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { ModelsActions } from './models.actions';
import { ModelService } from '../../services/model.service';
import { catchError, map, mergeMap, switchMap, debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { of } from 'rxjs';

@Injectable()
export class ModelsEffects {
  private actions$ = inject(Actions);
  private service = inject(ModelService);

  load$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ModelsActions.loadModels),
      switchMap(() =>
        this.service.getAll().pipe(
          map(models => ModelsActions.loadModelsSuccess({ models })),
          catchError(error => of(ModelsActions.loadModelsFailure({ error: error.message })))
        )
      )
    )
  );

  create$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ModelsActions.createModel),
      mergeMap(({ model }) =>
        this.service.create(model).pipe(
          map(created => ModelsActions.createModelSuccess({ model: created })),
          catchError(error => of(ModelsActions.createModelFailure({ error: error.error?.message ?? error.message })))
        )
      )
    )
  );

  update$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ModelsActions.updateModel),
      mergeMap(({ id, model }) =>
        this.service.update(id, model).pipe(
          map(updated => ModelsActions.updateModelSuccess({ model: updated })),
          catchError(error => of(ModelsActions.updateModelFailure({ error: error.message })))
        )
      )
    )
  );

  delete$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ModelsActions.deleteModel),
      mergeMap(({ id }) =>
        this.service.delete(id).pipe(
          map(() => ModelsActions.deleteModelSuccess({ id })),
          catchError(error => of(ModelsActions.deleteModelFailure({ error: error.message })))
        )
      )
    )
  );

  loadManufacturers$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ModelsActions.loadManufacturers),
      switchMap(() =>
        this.service.getManufacturers().pipe(
          map(manufacturers => ModelsActions.loadManufacturersSuccess({ manufacturers })),
          catchError(error => of(ModelsActions.loadManufacturersFailure({ error: error.message })))
        )
      )
    )
  );

  searchModels$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ModelsActions.searchModels),
      debounceTime(200),
      distinctUntilChanged((a, b) => a.manufacturer === b.manufacturer && a.q === b.q),
      switchMap(({ manufacturer, q }) =>
        this.service.searchModels(manufacturer, q).pipe(
          map(results => ModelsActions.searchModelsSuccess({ results })),
          catchError(error => of(ModelsActions.searchModelsFailure({ error: error.message })))
        )
      )
    )
  );
}
